import Sidebar from "#root/components/sidebar";
import { WithSidebar } from "#root/components/withSidebar";
import { productCollection, type ProductListItem } from "#root/schema/product";
import { vendorCollection } from "#root/schema/vendor";
import { useData } from "#root/utility/hooks";
import { leftJoin } from "#root/utility/joins";
import { flatToNamedTree } from "#root/utility/tree";
import type { ReactNode } from "react";
import { useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import * as Accordion from "../components/accordion";
import { GlyphLoading } from "../components/glyphs";
import { Markdown } from "../components/markdown";
import { ProductHighlights } from "../components/product";
import PageLayout from "./layout";

export default function ProductPage({ type, id }: { type: string; id: string }): ReactNode {
  const navigate = useNavigate();

  const [allProducts] = useData(async () => {
    const [{ rows: allProducts }, { rows: vendors }] = await Promise.all([productCollection.list({ listed: 1 }), vendorCollection.list()]);

    leftJoin({
      left: allProducts,
      right: vendors,
      leftKey: "vendorId",
      rightKey: "id",
      leftDest: "vendor",
    });

    return allProducts;
  }, []);

  const products = useMemo(() => allProducts?.filter(prod => prod.type.toUpperCase() === type.toUpperCase()), [allProducts, type]);

  const product = useMemo(() => allProducts?.find(item => String(item.id) === id || item.code === id), [allProducts, id]);

  useEffect(() => {
    if (product) {
      navigate(`/products/${product.type.toLowerCase()}/${product.code || product.id}`, { replace: true });
    } else if (id && allProducts) {
      navigate("/notFound", { replace: true });
    }
  }, [navigate, allProducts, id, product]);

  const title = useMemo(() => {
    if (product) {
      return product.name + { api: " API", dataset: "", visualization: "" }[type];
    }

    return { api: "API", dataset: "Data", visualization: "Visualization" }[type] + " Directory";
  }, [product, type]);

  if (!allProducts) {
    return (
      <PageLayout>
        <section>
          <GlyphLoading/>
        </section>
      </PageLayout>
    );
  }

  return (
    <PageLayout title={title} description={product?.description}>
      <WithSidebar>
        <ProductSidebar products={products!} type={type} id={id}/>
        <section>{product ? <ProductCard product={product}/> : <ProductList products={products} type={type}/>}</section>
      </WithSidebar>
    </PageLayout>
  );
}

function ProductList({ products, type }) {
  const [vendors, vendorNames] = useMemo(() => {
    if (!products) {
      return [];
    }

    const vendors = {};
    for (const product of products) {
      if (product.vendor) {
        const vendor = (vendors[product.vendor.name] ??= { products: [], ...product.vendor });
        vendor.products.push(product);
      }
    }

    return [vendors, Object.keys(vendors).sort()];
  }, [products]);

  if (!products) {
    return <div/>;
  }

  return (
    <div className="max-w-4xl">
      <h1 className="text-3xl font-bold text-gray-900 mb-2">
        {{ api: "API Directory", dataset: "Data Coverage", visualization: "Visualizations" }[type]}
      </h1>
      <p className="text-gray-500 mb-8 text-sm">
        {products.length} financial {{ api: "APIs", dataset: "Data Sets", visualization: "Visualizations" }[type]} for websites, mobile apps, desktop, and
        signage products. High quality data from leading financial data providers.
      </p>
      {vendorNames?.map(vendorName => {
        const vendor = vendors?.[vendorName];
        return (
          <div key={vendorName} className="mb-8">
            <h2 className="text-lg font-semibold text-gray-700 mb-3 pb-2 border-b border-gray-200">
              {vendorName} {{ api: "APIs", dataset: "", visualization: "Visualizations" }[type]}
            </h2>
            <ul className="space-y-3 list-none p-0">
              {vendor.products.map(prod => (
                <li key={prod.id} className="group">
                  <Link
                    className="font-medium "
                    to={"/products/" + type + "/" + prod.id}
                  >
                    {prod.name}
                  </Link>
                  <p className="text-sm text-gray-500 mt-0.5">{prod.description}</p>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

function ProductSidebar({ products, type, id }: { products: ProductListItem[]; type: string; id: string }) {
  const categories = useMemo(() => flatToNamedTree(products, p => p.category, p => p.name), [products]);

  const itemId = useMemo(() => {
    for (const product of products) {
      if (String(product.id) === id || product.code === id) {
        return product.id;
      }
    }
  }, [products, id]);

  return (
    <Sidebar>
      <Accordion.Parent initialItemId={itemId}>
        {categories.map(({ name, items }) => (
          <Accordion.Group key={name} label={name} groupId={name}>
            {items.map(product => (
              <Accordion.Item key={product.id} itemId={product.id}>
                <Link to={"/products/" + type + "/" + (product.code ? product.code : product.id)}>{product.name}</Link>
              </Accordion.Item>
            ))}
          </Accordion.Group>
        ))}
      </Accordion.Parent>
    </Sidebar>
  );
}

function ProductCard({ product }: { product: ProductListItem }) {
  const [details] = useData(() => productCollection.details(product.id), [product.id]);
  const [vendor] = useData(() => (product.vendorId ? vendorCollection.details(product.vendorId) : Promise.resolve(null)), [product.vendorId]);

  const detailRow = details?.row;
  const vendorRow = vendor?.row;

  if (!detailRow || !vendorRow) {
    return (
      <div className="flex items-center justify-center py-20">
        <GlyphLoading/>
      </div>
    );
  }

  // Const childProps = {productPath: '/products/' + product.category + '/' + product.name};

  return (
    <div className="max-w-4xl">
      <ProductHighlights detailRow={detailRow} vendorRow={vendorRow}/>
      <p className="text-gray-600 text-sm leading-relaxed mb-6">{detailRow.description}</p>
      <div className="mb-8">
        <Link to="/contact" className="inline-block bg-green-500 hover:bg-green-600 text-white font-semibold px-6 py-2 rounded transition-colors text-sm">
          TRY IT FREE
        </Link>
      </div>
      <div className="mb-8">
        <Markdown markdown={detailRow.page}/>
      </div>
      <div className="bg-gray-50 rounded-lg p-6 border border-gray-100">
        <Markdown markdown={vendorRow.profile}/>
      </div>
    </div>
  );
}
